import React, { useContext, useEffect, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import API from '../../../../services/API'
import {
  CircularProgress,
  Paper,
  Divider,
  Card,
  CardHeader,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CardContent,
  FormHelperText,
} from '@mui/material'
import { GlobalContext } from '../../../../App'
import UnsavedChanges from '../../../../components/UnsavedChanges/UnsavedChanges'
import ArchivedVehicles from './ArchivedVehicles'

const VehicleSettings = () => {
  const [vehicleTypes, setVehicleTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [changesPresent, setChangesPresent] = useState(false)
  const [servicingFrequency, setServicingFrequency] = useState('6')
  const [safetyCheckFrequency, setSafetyCheckFrequency] = useState('3')
  const [deepCleanFrequency, setDeepCleanFrequency] = useState(12)

  const { setToast } = useContext(GlobalContext)

  const loadBase = () => {
    API.get(
      '/settings?select=vehicleTypes deepCleanFrequency vehicleSafetyCheckFrequency vehicleServiceFrequency',
    ).then((res) => {
      if (res.status === 200) {
        setVehicleTypes(res.data.vehicleTypes || [])
        setServicingFrequency(res?.data?.vehicleServiceFrequency ?? '6')
        setSafetyCheckFrequency(res?.data?.vehicleSafetyCheckFrequency ?? '3')
        setDeepCleanFrequency(res?.data?.deepCleanFrequency ?? 12)
        setLoading(false)
        setChangesPresent(false)
      }
    })
  }

  useEffect(() => {
    loadBase()
  }, [])

  const updateSettings = () => {
    setLoading(true)
    API.put('/site-config', {
      vehicleTypes,
      vehicleServiceFrequency: servicingFrequency,
      vehicleSafetyCheckFrequency: safetyCheckFrequency,
      deepCleanFrequency,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Settings updated' })
          setChangesPresent(false)
        } else {
          setToast({
            message: 'Unable to update Vehicle Types. Please try again later.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to update Vehicle Types. Please try again later.',
        }),
      )
      .finally(() => setLoading(false))
  }

  return (
    <Stack gap={1}>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: '#374151',
            borderLeft: '4px solid #3b82f6',
            pl: 1.5,
          }}
        >
          Vehicle Types
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          Add or remove the vehicle types used across your organisation.
        </Typography>

        <Autocomplete
          value={vehicleTypes}
          options={[
            'Frontline Ambulance',
            'RRV',
            'Event Ambulance',
            'Bicycle',
            'Support Vehicle',
          ]}
          freeSolo
          multiple
          disabled={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Add a vehicle type..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          onChange={(e, newTypes) => {
            setVehicleTypes(newTypes)

            setChangesPresent(true)
          }}
        />
      </Stack>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: '#374151',
            borderLeft: '4px solid #3b82f6',
            pl: 1.5,
          }}
        >
          Vehicle Servicing Frequency
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          How often should vehicle services be completed?
        </Typography>
        <FormControl fullWidth>
          <Select
            value={servicingFrequency}
            onChange={(e) => {
              setServicingFrequency(e.target.value)
              setChangesPresent(true)
            }}
          >
            <MenuItem value="1">Every Month</MenuItem>
            <MenuItem value="2">Every Two Months</MenuItem>
            <MenuItem value="3">Every Three Months</MenuItem>
            <MenuItem value="6">Every Six Months</MenuItem>
            <MenuItem value="12">Every Year</MenuItem>
            <MenuItem value="18">Every 18 Months</MenuItem>
            <MenuItem value="24">Every Two Years</MenuItem>
            <MenuItem value="Disabled">Disabled</MenuItem>
          </Select>
          <FormHelperText>
            How often should vehicle services be completed?
          </FormHelperText>
        </FormControl>
      </Stack>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: '#374151',
            borderLeft: '4px solid #3b82f6',
            pl: 1.5,
          }}
        >
          Vehicle Safety Check Frequency
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          How often should safety checks be completed?
        </Typography>
        <FormControl fullWidth>
          <Select
            value={safetyCheckFrequency}
            onChange={(e) => {
              setSafetyCheckFrequency(e.target.value)
              setChangesPresent(true)
            }}
          >
            <MenuItem value="1">Every Month</MenuItem>
            <MenuItem value="2">Every Two Months</MenuItem>
            <MenuItem value="3">Every Three Months</MenuItem>
            <MenuItem value="6">Every Six Months</MenuItem>
            <MenuItem value="12">Every Year</MenuItem>
            <MenuItem value="18">Every 18 Months</MenuItem>
            <MenuItem value="24">Every Two Years</MenuItem>
            <MenuItem value="Disabled">Disabled</MenuItem>
          </Select>
          <FormHelperText>
            How often should safety checks be completed?
          </FormHelperText>
        </FormControl>
      </Stack>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: '#374151',
            borderLeft: '4px solid #3b82f6',
            pl: 1.5,
          }}
        >
          Deep Clean Frequency
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          How often should deep cleans be completed? You can configure further
          settings on the 'Vehicle Cleaning' page.
        </Typography>
        <FormControl fullWidth>
          <Select
            value={deepCleanFrequency}
            onChange={(e) => {
              setDeepCleanFrequency(e.target.value)
              setChangesPresent(true)
            }}
          >
            <MenuItem value={2}>2 weeks</MenuItem>
            <MenuItem value={4}>4 weeks</MenuItem>
            <MenuItem value={6}>6 weeks</MenuItem>
            <MenuItem value={8}>8 weeks</MenuItem>
            <MenuItem value={10}>10 weeks</MenuItem>
            <MenuItem value={12}>12 weeks</MenuItem>
            <MenuItem value={14}>14 weeks</MenuItem>
            <MenuItem value={16}>16 weeks</MenuItem>
            <MenuItem value={18}>18 weeks</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <ArchivedVehicles />
      <UnsavedChanges
        changesPresent={changesPresent}
        handleReset={() => loadBase()}
        handleSave={() => updateSettings()}
      />
    </Stack>
  )
}

export default VehicleSettings
